import type {
  ApiCommunityJSON,
  CommunityRole,
} from 'mastodon/api_types/communities';

// Mirror of Community::RANK_* on the server. This exists so the Manage screen
// can hide controls the viewer may not use — it is NOT the enforcement point.
// Every action these guard is independently refused by the server via
// Community#can_moderate? / #can_assign_role?, because a hidden button is a
// hint and a rule is something the server says no to.
export const RANK_NONE = 0;
export const RANK_MEMBER = 1;
export const RANK_MOD = 2;
export const RANK_ADMIN = 3;
export const RANK_CREATOR = 4;

export const rankOfRole = (role: CommunityRole | null | undefined): number => {
  switch (role) {
    case 'admin':
      return RANK_ADMIN;
    case 'mod':
      return RANK_MOD;
    case 'member':
      return RANK_MEMBER;
    default:
      return RANK_NONE;
  }
};

// The creator holds an ordinary admin membership row, so the role alone cannot
// identify them. Prefer the server's viewer_is_creator; fall back to comparing
// ids so this still behaves on a payload from a server that predates the field.
export const viewerRank = (
  community: Pick<
    ApiCommunityJSON,
    'viewer_role' | 'viewer_is_creator' | 'creator_account_id'
  >,
  viewerAccountId?: string | null,
): number => {
  const isCreator =
    community.viewer_is_creator ??
    (viewerAccountId != null &&
      community.creator_account_id === viewerAccountId);

  if (isCreator) return RANK_CREATOR;

  return rankOfRole(community.viewer_role);
};

export const memberRank = (
  role: CommunityRole | null | undefined,
  isCreator: boolean,
): number => (isCreator ? RANK_CREATOR : rankOfRole(role));

// Kick / ban / demote: only ever someone strictly below you.
export const canModerate = (actorRank: number, targetRank: number): boolean =>
  actorRank >= RANK_MOD && actorRank > targetRank;

// Assigning a role is bounded the same way at both ends, so only the creator
// can mint an admin.
export const canAssignRole = (
  actorRank: number,
  targetRank: number,
  role: CommunityRole,
): boolean =>
  canModerate(actorRank, targetRank) && actorRank > rankOfRole(role);

// Tab visibility, straight from the role table.
export const canAccessManage = (rank: number): boolean => rank >= RANK_MOD;
export const canViewMembers = (rank: number): boolean => rank >= RANK_MOD;
export const canViewReports = (rank: number): boolean => rank >= RANK_MOD;
export const canViewInvites = (rank: number): boolean => rank >= RANK_ADMIN;
export const canViewRequests = (rank: number): boolean => rank >= RANK_ADMIN;
export const canViewBans = (rank: number): boolean => rank >= RANK_ADMIN;
export const canRevokeBan = (rank: number): boolean => rank >= RANK_ADMIN;
export const canStartRoomAsAdmin = (rank: number): boolean =>
  rank >= RANK_ADMIN;
// Settings and Danger zone are the creator's alone.
export const canViewSettings = (rank: number): boolean => rank >= RANK_CREATOR;
export const canViewDangerZone = (rank: number): boolean =>
  rank >= RANK_CREATOR;
